import React, { useState } from "react";
import Logo from "shared/Logo/Logo";
import { useLocale } from "i18n/locale";
import ContactFormModal from "./ContactForm";
import { Alert, Snackbar } from "@mui/material";
import facebookIcon from "images/footer/facebook-glyph.svg";
import instagramIcon from "images/footer/instagram.svg";
import linkedinIcon from "images/footer/linkedin.svg";
import arrowRightIcon from "images/footer/arrow-right.svg";
import chevronRightIcon from "images/footer/chevron-right.svg";
import buildingIcon from "images/footer/building.svg";
import shieldIcon from "images/footer/shield.svg";
import lockIcon from "images/footer/lock.svg";
import allianzLogo from "images/footer/allianz.png";
import axaLogo from "images/footer/axa.png";

type FormValues = {
  name: string;
  email: string;
  phone: string;
  message: string;
  isChecked: boolean;
};

const HIDDEN_ROUTES = ["/pay-done", "/pay-failed", "/my-bookings"];

const Footer: React.FC = () => {
  const { t } = useLocale();
  const [formValues, setFormValues] = useState<FormValues>({
    name: "",
    email: "",
    phone: "",
    message: "",
    isChecked: false,
  });
  const [open, setOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleClose = () => {
    setOpen(false);
    setFormValues({ name: "", email: "", phone: "", message: "", isChecked: false });
  };

  if (HIDDEN_ROUTES.includes(window.location.pathname)) return null;

  return (
    <>
      <footer className="bg-[#faf7f1]">
        {/* Top section — GET IN TOUCH + Contact Us button */}
        <div className="px-4 sm:px-6 lg:px-[40px] pt-[30px] pb-[40px]">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-[24px]">
            <div className="flex flex-col gap-[23px] max-w-[387px]">
              <p
                className="text-[11px] leading-[24px] text-[#B88333] uppercase tracking-[2.2px]"
                style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
              >
                {t("footer.getInTouchLabel")}
              </p>
              <p
                className="text-[28px] leading-[38px] text-[#01152E]"
                style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
              >
                {t("footer.getInTouchHeading")}
              </p>
            </div>
            <button
              onClick={() => setOpen(true)}
              className="flex items-center justify-center gap-[10px] h-[44px] px-[24px] bg-[#01152E] border border-[#01152E] rounded-[30px] cursor-pointer flex-shrink-0"
            >
              <span
                className="text-[14px] leading-[24px] text-white whitespace-nowrap"
                style={{ fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
              >
                {t("footer.contactUs")}
              </span>
              <img src={arrowRightIcon} alt="" className="w-[13px] h-[12px]" />
            </button>
          </div>
        </div>

        {/* Separator */}
        <div className="px-4 sm:px-6 lg:px-[40px]">
          <div className="border-t border-[rgba(184,131,51,0.18)]" />
        </div>

        {/* Middle section — 4 columns */}
        <div className="px-4 sm:px-6 lg:px-[40px] py-[40px]">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-[0.9fr_1.1fr_1.1fr_0.9fr] gap-[20px] md:gap-[24px] xl:gap-[32px]">
            {/* Column 1 — Logo + company info + social */}
            <div className="min-w-0 flex flex-col gap-[17px]">
              <Logo className="w-[150px] [&_img]:w-full [&_img]:max-w-none" />
              <div>
                <p
                  className="text-[12px] leading-[18px] text-[#01152E] mb-[12px]"
                  style={{ fontFamily: "Roboto, sans-serif", fontWeight: 500 }}
                >
                  {t("footer.travelResidenceManagement")}
                </p>
                <p
                  className="text-[12px] leading-[18px] text-[#445162]"
                  style={{ fontFamily: "Roboto, sans-serif", fontWeight: 400 }}
                >
                  {t("footer.parisAndBrussels")}
                  <br />
                  {t("footer.hotelAparthotelAirbnb")}
                </p>
              </div>
              <div className="flex items-center gap-[10px]">
                <a
                  href="https://www.facebook.com/EuropeaResidences/?locale=fr_FR"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  className="flex items-center justify-center w-[36px] h-[36px] rounded-full border border-[#E0CDA6] hover:border-[#B88333] transition-colors"
                >
                  <img src={facebookIcon} alt="" className="w-[17px] h-[17px]" />
                </a>
                <a
                  href="https://www.instagram.com/europearesidences/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="flex items-center justify-center w-[36px] h-[36px] rounded-full border border-[#E0CDA6] hover:border-[#B88333] transition-colors"
                >
                  <img src={instagramIcon} alt="" className="w-[17px] h-[17px]" />
                </a>
                <a
                  href="https://www.linkedin.com/company/europea-residences/?viewAsMember=true"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  className="flex items-center justify-center w-[36px] h-[36px] rounded-full border border-[#E0CDA6] hover:border-[#B88333] transition-colors"
                >
                  <img src={linkedinIcon} alt="" className="w-[17px] h-[17px]" />
                </a>
              </div>
            </div>

            {/* Column 2 — Our Group Companies */}
            <div className="min-w-0 flex flex-col gap-[14px] rounded-[16px] border border-[rgba(184,131,51,0.12)] bg-white/25 p-[18px]">
              <div className="flex items-center gap-[10px]">
                <span className="flex items-center justify-center w-[38px] h-[38px] rounded-full border border-[#D8BF8E] flex-shrink-0">
                  <img src={buildingIcon} alt="" className="w-[20px] h-[20px]" />
                </span>
                <p
                  className="text-[11px] leading-[24px] text-[#B88333] uppercase tracking-[2.2px] whitespace-nowrap"
                  style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
                >
                  {t("footer.ourGroupCompanies")}
                </p>
              </div>
              <div className="flex flex-col">
                {[
                  {
                    name: "Europea Residences SA",
                    desc: t("footer.companies.reservationDistribution"),
                  },
                  {
                    name: "Europea Management SRL",
                    desc: t("footer.companies.belgiumPropertyManagement"),
                  },
                  {
                    name: "Rent EURL",
                    desc: t("footer.companies.francePropertyManagement"),
                  },
                ].map((company, i) => (
                  <div
                    key={company.name}
                    className={i > 0 ? "border-t border-[rgba(184,131,51,0.08)] pt-[12px] mt-[12px]" : ""}
                  >
                    <p
                      className="text-[13px] leading-[18px] text-[#01152E] mb-[2px]"
                      style={{ fontFamily: "Roboto, sans-serif", fontWeight: 600 }}
                    >
                      {company.name}
                    </p>
                    <p
                      className="text-[12px] leading-[18px] text-[#445162]"
                      style={{ fontFamily: "Roboto, sans-serif", fontWeight: 400 }}
                    >
                      {company.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Column 3 — Professional Guarantees */}
            <div className="min-w-0 flex flex-col gap-[14px] rounded-[16px] border border-[rgba(184,131,51,0.12)] bg-white/25 p-[18px]">
              <div className="flex items-center gap-[10px]">
                <span className="flex items-center justify-center w-[38px] h-[38px] rounded-full border border-[#D8BF8E] flex-shrink-0">
                  <img src={shieldIcon} alt="" className="w-[20px] h-[20px]" />
                </span>
                <p
                  className="text-[11px] leading-[24px] text-[#B88333] uppercase tracking-[2.2px] whitespace-nowrap"
                  style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
                >
                  {t("footer.professionalGuarantees")}
                </p>
              </div>
              <div className="flex flex-col">
                {/* Allianz */}
                <div className="flex items-center gap-[14px]">
                  <div className="w-[92px] flex-shrink-0 flex items-center">
                    <img src={allianzLogo} alt="Allianz" className="w-[88px] h-auto" />
                  </div>
                  <div>
                    <p
                      className="text-[12px] leading-[16px] text-[#01152E]"
                      style={{ fontFamily: "Roboto, sans-serif", fontWeight: 500 }}
                    >
                      {t("footer.guarantees.allianzName")}
                    </p>
                    <p
                      className="text-[12px] leading-[16px] text-[#445162]"
                      style={{ fontFamily: "Roboto, sans-serif", fontWeight: 400 }}
                    >
                      {t("footer.guarantees.allianzPolicy")}
                    </p>
                  </div>
                </div>
                {/* AXA */}
                <div className="flex items-center gap-[14px] border-t border-[rgba(184,131,51,0.08)] pt-[12px] mt-[12px]">
                  <div className="w-[92px] flex-shrink-0 flex items-center">
                    <img src={axaLogo} alt="AXA" className="w-[44px] h-auto" />
                  </div>
                  <div>
                    <p
                      className="text-[12px] leading-[16px] text-[#01152E]"
                      style={{ fontFamily: "Roboto, sans-serif", fontWeight: 500 }}
                    >
                      {t("footer.guarantees.axaName")}
                    </p>
                    <p
                      className="text-[12px] leading-[16px] text-[#445162]"
                      style={{ fontFamily: "Roboto, sans-serif", fontWeight: 400 }}
                    >
                      {t("footer.guarantees.axaPolicy")}
                    </p>
                  </div>
                </div>
                {/* SOCAF — TODO: replace text wordmark with socaf.png once the logo asset is provided */}
                <div className="flex items-center gap-[14px] border-t border-[rgba(184,131,51,0.08)] pt-[12px] mt-[12px]">
                  <div className="w-[92px] flex-shrink-0 flex items-center">
                    <span
                      className="text-[19px] leading-[19px] text-[#01152E] tracking-[1px]"
                      style={{ fontFamily: "Poppins, sans-serif", fontWeight: 700 }}
                    >
                      SOCAF
                    </span>
                  </div>
                  <div>
                    <p
                      className="text-[12px] leading-[16px] text-[#01152E]"
                      style={{ fontFamily: "Roboto, sans-serif", fontWeight: 500 }}
                    >
                      {t("footer.guarantees.socafName")}
                    </p>
                    <p
                      className="text-[12px] leading-[16px] text-[#445162]"
                      style={{ fontFamily: "Roboto, sans-serif", fontWeight: 400 }}
                    >
                      {t("footer.guarantees.socafPolicy")}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Column 4 — Important Links. items-end/pt-[26px]/w-[210px] (right-aligned,
                narrow, aligned with the card headers) only make sense once this sits
                as its own rightmost column in the xl 4-column grid — below that it
                shares a row/stacks with the other columns via grid-cols-1/md:grid-cols-2,
                where right-aligning it just pushes it away from their shared left
                margin. Left-align and go full width until xl. */}
            <div className="min-w-0 flex flex-col gap-[20px] items-start xl:items-end xl:pt-[26px]">
              <div className="flex flex-col gap-[20px] w-full xl:w-[210px]">
                <p
                  className="text-[11px] leading-[24px] text-[#B88333] uppercase tracking-[2.2px]"
                  style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
                >
                  {t("footer.importantLinks")}
                </p>
                <div
                  className="flex flex-col gap-[16px] text-[12px] text-[#01152E]"
                  style={{ fontFamily: "Roboto, sans-serif", fontWeight: 500 }}
                >
                  {[
                    {
                      label: t("footer.links.joinOwner"),
                      href: "https://www.europeaforowner.com",
                      external: true,
                    },
                    { label: t("footer.links.travelTerms"), href: "/travel-terms-of-use" },
                    { label: t("footer.links.residenceTerms"), href: "/terms-of-use" },
                    { label: t("footer.links.faq"), href: "/legal" },
                  ].map((link) => (
                    <a
                      key={link.label}
                      href={link.href}
                      {...(link.external
                        ? { target: "_blank", rel: "noopener noreferrer" }
                        : {})}
                      className="flex items-center justify-between gap-[10px] leading-[16px] hover:text-[#B88333] transition-colors"
                    >
                      <span>{link.label}</span>
                      <img src={chevronRightIcon} alt="" className="w-[7px] h-[12px] flex-shrink-0" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Separator */}
        <div className="px-4 sm:px-6 lg:px-[40px]">
          <div className="border-t border-[rgba(184,131,51,0.18)]" />
        </div>

        {/* Bottom — Copyright + secondary links */}
        <div className="px-4 sm:px-6 lg:px-[40px] py-[26px]">
          <div className="flex flex-col-reverse sm:flex-row sm:items-center justify-between gap-[16px]">
            <p
              className="text-[12px] leading-[18px] text-[#445162] text-center sm:text-left"
              style={{ fontFamily: "Roboto, sans-serif", fontWeight: 400 }}
            >
              {t("footer.copyright")}
            </p>
            <div
              className="flex flex-wrap items-center justify-center sm:justify-end gap-x-[16px] gap-y-[10px] text-[12px] leading-[18px] text-[#445162]"
              style={{ fontFamily: "Roboto, sans-serif", fontWeight: 400 }}
            >
              <span aria-hidden className="hidden sm:block w-px h-[14px] bg-[#B88333] flex-shrink-0" />
              <a href="#" className="flex items-center gap-[6px] whitespace-nowrap hover:text-[#B88333] transition-colors">
                <img src={lockIcon} alt="" className="w-[17px] h-[17px]" />
                <span>{t("footer.secureBooking")}</span>
              </a>
              <span aria-hidden className="w-px h-[14px] bg-[#B88333] flex-shrink-0" />
              <a href="#" className="whitespace-nowrap hover:text-[#B88333] transition-colors">
                {t("footer.privacyPolicy")}
              </a>
              <span aria-hidden className="w-px h-[14px] bg-[#B88333] flex-shrink-0" />
              <a href="#" className="whitespace-nowrap hover:text-[#B88333] transition-colors">
                {t("footer.termsConditions")}
              </a>
            </div>
          </div>
        </div>
      </footer>

      <ContactFormModal
        setSnackbarSeverity={setSnackbarSeverity}
        setSnackbarMessage={setSnackbarMessage}
        setSnackbarOpen={setSnackbarOpen}
        formValues={formValues}
        setFormValues={setFormValues}
        isOpen={open}
        onClose={handleClose}
        showThankYouOnSuccess
      />

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Footer;
